<template>
  <PageWrap
    :class="[
      $style.base,
      {
        [$style.smallScreen]: $screen === 's'
      }
    ]"
    :heading="$t('admin.nav.links.company')"
    useForm
    @submit="submitForm"
  >
    <Flex direction="column">
      <BaseCard>
        <Flex direction="column">
          <BaseInput
            v-model="formData.name"
            :label="$t('company.name')"
            required
            :info="
            hasFeatureFlag('franchise-scope') ? $t('company.name_info') : ''
          "
            :minLength="2"
            :maxLength="255"
            :error="companyNameError"
            v-test="'company-settings-name'"
          />
          <BaseInput
            v-if="hasFeatureFlag('franchise-scope')"
            v-model="formData.internalName"
            :label="$t('company.internal_name')"
            required
            :info="$t('company.internal_name_info', brandName())"
            :minLength="2"
            :maxLength="255"
            v-test="'company-settings-internalName'"
          />
          <AddressFields
            v-model:address="formData.address"
            v-model:zipcode="formData.postalcode"
            v-model:city="formData.city"
            testId="company-settings"
          />
          <BaseInput
            v-model="formData.phone"
            :label="$t('company.phone_number')"
            type="tel"
            :regex="COMPANY_PHONE_VALIDATION_REGEX"
            v-test="'company-settings-phone'"
          />
          <BaseInputLabel
            :text="t('company.logo')"
            :info="t('company.info_image_autoresize')"
            v-test="'_form-element-label'"
          />
          <div :class="[$style.row]">
            <BaseImage
              v-if="!!currentLogoImageUrl"
              :cloudinaryId="currentLogoImageUrl"
              :width="120"
              :height="120"
            />
            <svg
              v-if="!currentLogoImageUrl"
              :class="$style.logoPlaceholder"
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                fill="#231F20"
                fill-rule="evenodd"
                d="M8 10a1.5 1.5 0 1 0-.001-3.001A1.5 1.5 0 0 0 8 10Zm10 9H6.561l7.005-5.845c.246-.209.692-.208.933-.001L19 16.994V18a1 1 0 0 1-1 1ZM6 5h12a1 1 0 0 1 1 1v8.364l-3.203-2.732c-.99-.842-2.539-.842-3.52-.006L5 17.698V6a1 1 0 0 1 1-1Zm12-2H6C4.346 3 3 4.346 3 6v12c0 1.654 1.346 3 3 3h12c1.654 0 3-1.346 3-3V6c0-1.654-1.346-3-3-3Z"
                clip-rule="evenodd"
              />
              <mask
                id="a"
                width="18"
                height="18"
                x="3"
                y="3"
                maskUnits="userSpaceOnUse"
                style="mask-type: luminance"
              >
                <path
                  fill="#fff"
                  fill-rule="evenodd"
                  d="M8 10a1.5 1.5 0 1 0-.001-3.001A1.5 1.5 0 0 0 8 10Zm10 9H6.561l7.005-5.845c.246-.209.692-.208.933-.001L19 16.994V18a1 1 0 0 1-1 1ZM6 5h12a1 1 0 0 1 1 1v8.364l-3.203-2.732c-.99-.842-2.539-.842-3.52-.006L5 17.698V6a1 1 0 0 1 1-1Zm12-2H6C4.346 3 3 4.346 3 6v12c0 1.654 1.346 3 3 3h12c1.654 0 3-1.346 3-3V6c0-1.654-1.346-3-3-3Z"
                  clip-rule="evenodd"
                />
              </mask>
              <g mask="url(#a)">
                <path d="M0 0h24v24H0z" />
              </g>
            </svg>
            <BaseUpload
              preset="company_logo"
              :hasImage="!!currentLogoImageUrl"
              @uploadResults="handleUpload"
            />
            <BaseButton
              v-if="!!currentLogoImageUrl"
              color="inverted"
              @click="
              () => {
                formData.logoUrl = null;
                currentLogoImageUrl = undefined;
              }
            "
            >
              {{ t('global.actions.delete') }}
            </BaseButton>
          </div>
          <TaxPayerEntitySettings
            v-if="company.country === 'de' && company.activeSubscription"
          />
          <VatIdSetting v-else-if="company.activeSubscription" />
        </Flex>
      </BaseCard>

      <BaseCard :heading="t('company.email_settings')">
        <Flex direction="column">
          <BaseInput
            v-model="formData.notificationEmail"
            :label="$t('company.notification_email_address')"
            :info="$t('company.notification_email_address_info')"
            type="email"
            required
            v-test="'company-settings-notificationEmail'"
          />
          <BaseSpinner
            v-if="loading"
            inline
          />
          <BaseInput
            v-else
            v-model="formData.billingInfo.billingEmail"
            :label="$t('company.billing_email_address')"
            :info="$t('company.billing_email_address_info')"
            type="email"
            v-test="'company-settings-billingEmail'"
          />
          <div v-if="unleash.isEnabled('AmazonSES-CustomDomain')">
            <BaseVisualBanner
              :heading="$t('company.contact_email')"
              :description="$t('company.domain_settings_disclaimer')"
              :primaryAction="{text: $t('company.domain_settings'), routerLink: {
              name: 'email-verification'
            }}"
              size="s"
              imagePath="/img/empty-states/messages.svg"
              v-test="'amazon-domain-settings-link'"
            />
          </div>
          <div v-else>
            <BaseInput
              v-model="formData.contactEmail"
              :label="$t('company.contact_email')"
              :info="$t('company.contact_email_info')"
              type="email"
              required
              v-test="'company-settings-contactEmail'"
            />
            <BaseSpinner
              v-if="loading"
              inline
            />
            <BaseText
              v-else
              inline
              @click="handleEmailDomainClick"
              v-test="'domain-settings-link'"
            >
              {{ $t('company.domain_settings') }}
            </BaseText>
          </div>
        </Flex>
      </BaseCard>

      <BaseCard :heading="$t('company.sms_settings')">
        <div :class="[$style.formRow, $style.mobileFull]">
          <BaseInput
            v-model="formData.smsSenderId"
            :label="$t('company.sms_message_sender')"
            :info="$t('company.sms_message_sender_info')"
            required
            :minLength="2"
            :maxLength="smsSenderIdMaxLength"
            v-test="'company-settings-smsSenderId'"
          />
          <BaseInput
            v-model="formData.smsNotificationNumber"
            :label="$t('company.sms_notifications_number')"
            :info="$t('company.sms_notifications_number_info')"
            type="tel"
            :maxLength="20"
            v-test="'company-settings-smsNotificationNumber'"
          />
        </div>
      </BaseCard>

      <BaseCard :heading="$t('company.social_information')">
        <Flex direction="column">
          <BaseText>{{ $t('company.info_social_info') }}</BaseText>
          <div
            :class="[
            $style.row,
            {
              [$style.mediumScreen]: $screen !== 'l'
            }
          ]"
          >
            <div :class="[$style.grow, $style.socialInput]">
              <BaseText>https://www.facebook.com/</BaseText>
              <div :class="$style.grow">
                <BaseInput
                  v-model="formData.facebookId"
                  :maxLength="255"
                  v-test="'company-settings-facebookId'"
                />
              </div>
            </div>
            <img
              :class="$style.socialImage"
              src="/img/social/profile_facebook_desc.png"
            >
          </div>
          <div
            :class="[
            $style.row,
            {
              [$style.mediumScreen]: $screen !== 'l'
            }
          ]"
          >
            <div :class="[$style.grow, $style.socialInput]">
              <BaseText>https://www.twitter.com/</BaseText>
              <div :class="$style.grow">
                <BaseInput
                  v-model="formData.twitterId"
                  :maxLength="255"
                  v-test="'company-settings-twitterId'"
                />
              </div>
            </div>
            <img
              :class="$style.socialImage"
              src="/img/social/profile_twitter_desc.png"
            >
          </div>
          <div
            :class="[
            $style.row,
            {
              [$style.mediumScreen]: $screen !== 'l'
            }
          ]"
          >
            <div :class="[$style.grow, $style.socialInput]">
              <BaseText>https://www.instagram.com/</BaseText>
              <div :class="$style.grow">
                <BaseInput
                  v-model="formData.instagramId"
                  :maxLength="255"
                  v-test="'company-settings-instagramId'"
                />
              </div>
            </div>
            <img
              :class="$style.socialImage"
              src="/img/social/profile_instagram_desc.png"
            >
          </div>
        </Flex>
      </BaseCard>
    </Flex>

    <template #actions>
      <BaseButton
        :loading="isSubmitting"
        submitForm
        :fullWidth="$screen === 's'"
        v-test="'company-settings-submit'"
      >
        {{ $t('global.actions.save') }}
      </BaseButton>
    </template>

    <EmailDomainModal
      v-if="showEmailDomainModal"
      @close="showEmailDomainModal = false"
    />
  </PageWrap>
</template>

<script lang="ts">
export default {
  name: 'CompanySettings',
};
</script>

<script
  lang="ts"
  setup
>
import { useMutation, useQuery } from '@vue/apollo-composable';
import gql from 'graphql-tag';

import { computed, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import AddressFields from '@/components/_shared/address-fields/index.vue';
import type { CloudinaryResponse } from '@/components/base-upload/index.vue';
import eventBus from '@/event-bus';
import { brandName } from '@/helpers/brand-name';
import { COMPANY_PHONE_VALIDATION_REGEX } from '@/helpers/regex';
import { flash } from '@/helpers/ui';
import { useCompanyStore } from '@/stores/company';
import { useUserStore } from '@/stores/user';
import type { BillingInfoAttributes, UpdateCompanyInput } from '@/types';
import unleash from '@/unleash';
import PageWrap from '../PageWrap.vue';
import EmailDomainModal from './EmailDomainModal.vue';
import TaxPayerEntitySettings from './TaxPayerEntitySettings.vue';
import VatIdSetting from './VatIdSetting.vue';

type UpdateCompanyFormData = UpdateCompanyInput & {
  billingInfo: Partial<BillingInfoAttributes>;
};

const { t } = useI18n();
const { company, updateCompany } = useCompanyStore();
const { hasFeatureFlag } = useUserStore();

const currentLogoImageUrl = ref<string | undefined>(
  company.logo?.thumbnail || undefined,
);

const isSubmitting = ref(false);
const companyNameError = ref<string | null>(null);

const router = useRouter();
const showEmailDomainModal = ref(false);

const { result, loading, onResult } = useQuery(gql`
  query getCurrentUser {
    currentUser {
      company {
        billingInfo {
          billingEmail
        }
        emailDomain {
          id
        }
      }
    }
  }
`);

const hasEmailDomain = computed(
  () => !!result.value?.currentUser.company.emailDomain?.id,
);

const handleEmailDomainClick = () => {
  if (hasEmailDomain.value) {
    if (unleash.isEnabled('AmazonSES-CustomDomain')) {
      router.push({ name: 'email-verification' });
    } else {
      router.push({ name: 'admin-company-communication' });
    }
  } else {
    showEmailDomainModal.value = true;
  }
};

const formData = reactive<UpdateCompanyFormData>({
  name: company.name,
  internalName: company.internalName,
  address: company.address,
  city: company.city,
  postalcode: company.postalcode,
  phone: company.phone,
  notificationEmail: company.notificationEmail,
  contactEmail: company.contactEmail,
  billingInfo: {
    billingEmail: company.billingInfo?.billingEmail,
  },
  logoUrl: company.logoUrl,
  smsSenderId: company.smsSenderId,
  smsNotificationNumber: company.smsNotificationNumber,
  facebookId: company.facebookId,
  twitterId: company.twitterId,
  instagramId: company.instagramId,
});

onResult(
  ({
    data: {
      currentUser: { company },
    },
  }) => {
    formData.billingInfo.billingEmail = company.billingInfo.billingEmail;
  },
);

const smsSenderIdMaxLength = computed(() =>
  formData?.smsSenderId?.match(/^\d+$/) ? 13 : 11,
);

watch(
  () => formData.name,
  () => {
    companyNameError.value = null;
  },
);

const handleUpload = (uploadResults: CloudinaryResponse) => {
  formData.logoUrl = `${uploadResults.resource_type}/${uploadResults.type}/${uploadResults.public_id}.${uploadResults.format}`;
  currentLogoImageUrl.value = uploadResults.secure_url;
};

const { mutate: updateTask } = useMutation(gql`
  mutation updateOnboardingTask($input: UpdateOnboardingTaskInput!) {
    updateOnboardingTask(input: $input) {
      onboardingTask {
        id
        task
        completedAt
        claimedAt
      }
    }
  }
`);

const submitForm = () => {
  isSubmitting.value = true;

  updateCompany(formData)
    .then(() => {
      updateTask({
        input: {
          task: 'SETUP_COMPANY_DETAILS',
          taskStatus: 'COMPLETE',
        },
      }).then(() => {
        eventBus.$emit('update-task', 'setup_company_details');
      });

      if (
        hasFeatureFlag('franchise-scope') &&
        company.internalName !== formData.internalName
      ) {
        eventBus.$emit('update-franchise-name', formData.internalName);
      }

      flash(t('global.flash.company_updated'));
    })
    .catch((errors) => {
      const companyNameErr = errors?.find(
        (err: any) => err.attribute === 'name',
      );
      if (companyNameErr) {
        companyNameError.value = t('company.errors.invalid_company_name');
      }
    })
    .finally(() => {
      isSubmitting.value = false;
    });
};
</script>

<style
  lang="scss"
  module
>
.formRow {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: $spacing;

  & > * {
    width: calc(50% - #{$spacing * 0.5});
  }

  &.mobileFull {
    .base.smallScreen & {
      & > * {
        width: 100%;
      }
    }
  }
}

.grow {
  flex-grow: 1;
  align-self: stretch;
}

.row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: $spacing;

  &.mediumScreen {
    flex-direction: column;
    align-items: baseline;
  }
}

.logoPlaceholder {
  opacity: 0.6;
  width: 120px;
  height: 120px;
  fill: var(--color-text);
  border: 1px solid var(--color-text);
  padding: 20px;
}

.socialInput {
  display: flex;
  align-items: center;
  align-self: stretch;
  gap: $spacing;

  & > *:first-child {
    width: 180px;
  }
}

.socialImage {
  width: 270px;
}
</style>
